import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface IngestSourceSummary {
  id: string;
  provider: string;
  display_path: string;
  include_subfolders: boolean;
  file_types: string[] | null;
  enabled: boolean;
  last_status: string | null;
  last_error: string | null;
  last_synced_at: string | null;
  files_total: number;
  files_extracted: number;
  files_failed: number;
  files_removed: number;
  files_analyzed: number;
  chunks_total: number;
  integration_active: boolean;
}

export interface IngestedFile {
  id: string;
  source_id: string;
  provider: string;
  source_path: string | null;
  file_name: string;
  mime_type: string | null;
  extraction_status: string;
  embedding_status: string;
  extraction_error: string | null;
  truncated: boolean;
  removed_at: string | null;
  source_modified_at: string | null;
  updated_at: string;
  chunk_count: number;
  candidate_count: number;
  text_preview: string | null;
}

export const FILES_PAGE_SIZE = 100;

type RpcResult<T> = Promise<{ data: T | null; error: { message: string; code?: string } | null }>;

// Migration 115's RPCs are not in the generated Supabase types (which must not
// be edited), so call them through a narrow typed wrapper.
const rpc = <T,>(fn: string, args?: Record<string, unknown>) =>
  (supabase.rpc as unknown as (f: string, a?: Record<string, unknown>) => RpcResult<T>)(fn, args);

const isForbidden = (e: { message: string; code?: string }) =>
  e.code === '42501' || /not authorized/i.test(e.message);

/**
 * Sources + ingested files for the Document Intelligence tab. `enabled` should be
 * the caller's review-role check; when false (or the RPCs reject the caller) the
 * hook stays quiet and reports `forbidden` so the UI can hide the sections.
 */
export const useIngestedDocuments = (enabled: boolean) => {
  const [sources, setSources] = useState<IngestSourceSummary[]>([]);
  const [files, setFiles] = useState<IngestedFile[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // `error` = initial-load failure only (replaces the sources strip). A failed
  // "Load more" is `loadMoreError` so the already-loaded sources/files stay visible.
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    const mine = ++seq.current;
    setLoading(true);
    setLoadMoreError(null);
    const [s, f] = await Promise.all([
      rpc<IngestSourceSummary[]>('di_list_sources'),
      rpc<IngestedFile[]>('di_list_files', { p_limit: FILES_PAGE_SIZE, p_offset: 0 }),
    ]);
    if (mine !== seq.current) return;
    const err = s.error ?? f.error;
    if (err) {
      if (isForbidden(err)) { setForbidden(true); setError(null); }
      else { console.error('Failed to load ingested documents:', err); setError('Could not load data sources.'); }
      setSources([]); setFiles([]); setHasMore(false);
    } else {
      setForbidden(false); setError(null);
      setSources(s.data ?? []);
      setFiles(f.data ?? []);
      setHasMore((f.data ?? []).length >= FILES_PAGE_SIZE);
    }
    setLoading(false);
  }, [enabled]);

  const loadMore = useCallback(async () => {
    if (!enabled || loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    setLoadMoreError(null);
    const { data, error: err } = await rpc<IngestedFile[]>('di_list_files', {
      p_limit: FILES_PAGE_SIZE, p_offset: files.length,
    });
    if (mine === seq.current) {
      if (err) { console.error('Failed to load more files:', err); setLoadMoreError('Could not load more files.'); }
      else {
        const next = data ?? [];
        setFiles((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          return [...prev, ...next.filter((x) => !seen.has(x.id))];
        });
        setHasMore(next.length >= FILES_PAGE_SIZE);
      }
    }
    setLoadingMore(false);
  }, [enabled, loadingMore, files.length]);

  useEffect(() => { void refresh(); }, [refresh]);

  return { sources, files, loading, loadingMore, hasMore, error, loadMoreError, forbidden, refresh, loadMore };
};
